import moment from "moment";
import React, { useContext, useState } from "react";
import { FaCheckCircle, FaEdit, FaExclamationTriangle, FaInfoCircle, FaSpinner, FaTimes, FaTimesCircle, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import styled from "styled-components";
import { deleteTimeSheet, getTimeSheet, modifyTimeEntry, selectLoading } from "../common/slices/timesheet";
import { ErrorContext } from "../ErrorContext";
import formatCurrency from "../utils/formatCurrency";
import { Attribute } from "./Attribute";
import { ButtonBase } from "./Buttons";
import Divider from "./Divider";
import Modal from "./Modal";
import ModalConfirm from "./ModalConfirm";
import ModalEditTimesheet from "./ModalEditTimesheet";
import { Pill } from "./Pill";
import Row from "./Row";
import { Table, TableData, TableHeader, TableRow } from "./Table";
import { H1, P, P2 } from "./Typography";
import theme from "../common/theme";
import AlertBox from "./AlertBox";
import { orderBy } from 'lodash';

const Footer = styled.tfoot`
    td {
        border: none;
        font-weight: 700;
    }
`

const ModalTimesheet = ({ timesheet, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const loading = useSelector(selectLoading);
    const dispatch = useDispatch();
    const [showEdit, setShowEdit] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const totals = timesheet?.timeEntries?.items?.reduce((totals, itr, idx) => {
        const rate = itr.crewMember.rateOverride?.rate || itr.crewMember?.rate
        const jobCost = (itr.adjustedHours.straightTime * rate) + (itr.adjustedHours.overtime * 1.5 * rate)

        return {
            hours: totals.hours + itr.hours,
            straightTime: totals.straightTime + itr.adjustedHours.straightTime,
            overtime: totals.overtime + itr.adjustedHours.overtime,
            jobCost: totals.jobCost + jobCost,
            rate: totals.rate + (rate),
        }
    }, { hours: 0, straightTime: 0, overtime: 0, jobCost: 0, rate: 0 })

    const onTimeEntryClick = async (timeEntry) => {
        try {
            if (timeEntry.status === "PENDING") {
                await dispatch(modifyTimeEntry({ ...timeEntry, isApproved: !!!timeEntry.isApproved, }))
                toast.success("Saved Time Entry");
                dispatch(getTimeSheet(timesheet.id));
            }
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to save time entry", forSupport: ex.message })
        }
    }

    const onApproveAll = async () => {
        try {
            const pendingItems = timesheet?.timeEntries?.items.filter(te => te.status === "PENDING");
            await Promise.all(pendingItems.map(te => dispatch(modifyTimeEntry({ ...te, isApproved: true }))))

            await dispatch(getTimeSheet(timesheet.id));
            toast.success("Approved all time entries");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to approve all time sheet", forSupport: ex.message })
        }
    }

    const onDeleteTimesheet = async () => {
        try {
            await dispatch(deleteTimeSheet(timesheet));
            onClose();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to delete time sheet", forSupport: ex.message })
        }
    }

    return (
        <Modal show size="75%" onClose={onClose} closeOnEscape>
            <Row>
                <H1>View Time Sheet</H1>
                <ButtonBase onClick={onClose} style={{ marginLeft: 'auto' }} title="Close"><FaTimes size='16px' color="#000" /></ButtonBase>
            </Row>

            <Row>
                <ButtonBase style={{ padding: "0.5rem" }} onClick={onApproveAll} title="Approve All Time Entries">
                    <FaCheckCircle size={"14px"} color="#000" />
                </ButtonBase>
                <Divider.Vertical />
                <ButtonBase style={{ padding: "0.5rem" }} onClick={() => setShowEdit(true)} title="Edit Time Sheet">
                    <FaEdit size={"14px"} color="#000" />
                </ButtonBase>
                <Divider.Vertical />
                <ButtonBase style={{ padding: "0.5rem" }} onClick={() => setShowConfirm(true)} title="Delete Time Sheet">
                    <FaTrash size={"14px"} color="#000" />
                </ButtonBase>
            </Row>

            <Row>
                <Attribute>
                    <P2>Customers</P2>
                    <P>{timesheet?.customers?.map(cx => cx.name).join(', ')}</P>
                </Attribute>
                <Attribute>
                    <P2>All Approved</P2>
                    {timesheet?.allApproved ? <Pill>Yes</Pill> : <Pill>No</Pill>}
                </Attribute>
                <Attribute>
                    <P2>Location</P2>
                    <P>{timesheet?.location?.name}</P>
                </Attribute>
            </Row>


            <Row>
                <Attribute>
                    <P2>Date Submitted</P2>
                    <P>{moment(timesheet?.date).format("YYYY-MM-DD [at] h:mm A")}</P>
                </Attribute>
                <Attribute>
                    <P2>Foreman</P2>
                    <P>{timesheet?.foreman?.active ? '' : <FaTimesCircle title="Inactive User" />} {timesheet?.foreman?.name}</P>
                </Attribute>
                <Attribute>
                    <P2>Created By</P2>
                    <P>{timesheet?.createdBy?.firstName} {timesheet?.createdBy?.lastName}</P>
                </Attribute>
            </Row>

            <Divider.Horizontal />

            <Table>
                <thead>
                    <tr>
                        <TableHeader colSpan={3}>Crew Member</TableHeader>
                        <TableHeader>Customer</TableHeader>
                        <TableHeader>Scope of Work</TableHeader>
                        <TableHeader>Rate /hr</TableHeader>
                        <TableHeader>Hours</TableHeader>
                        <TableHeader>Straight Time</TableHeader>
                        <TableHeader>Overtime</TableHeader>
                        <TableHeader>Job Cost</TableHeader>
                    </tr>
                </thead>

                <tbody>
                    {orderBy(timesheet?.timeEntries?.items, 'crewMember.name').map((timeEntry) => {
                        const rate = timeEntry.crewMember?.rateOverride?.rate || timeEntry.crewMember?.rate
                        const jobCost = (timeEntry.adjustedHours.straightTime * rate) + (timeEntry.adjustedHours.overtime * 1.5 * rate)

                        return (
                            <TableRow key={timeEntry.id} onClick={() => onTimeEntryClick(timeEntry)}>
                                <TableData style={{ width: '1rem' }}>
                                    {timeEntry.isApproved ? <FaCheckCircle title="Approved" /> : <FaExclamationTriangle title="Not Approved" />}
                                </TableData>
                                <TableData style={{ width: '1rem' }}>
                                    {timeEntry.status === 'SYNC_COMPLETE' && <FaCheckCircle title={`Sync Complete - ${timeEntry.syncStatusMessage}`} />}
                                    {timeEntry.status === 'ERROR' && <FaExclamationTriangle title={`Sync Error - ${timeEntry.syncStatusMessage}`} />}
                                </TableData>
                                <TableData>{timeEntry.crewMember?.active ? '' : <FaTimesCircle title="Inactive User" style={{ marginRight: '0.25rem' }} />}{timeEntry.crewMember?.name}</TableData>
                                <TableData>{timeEntry.customer?.name}</TableData>
                                <TableData>{timeEntry.scope?.name}</TableData>
                                <TableData>{formatCurrency(rate)}</TableData>
                                <TableData>{moment.duration({ hours: timeEntry.hours }).format("*h:mm [h]")}</TableData>
                                <TableData>{moment.duration({ hours: timeEntry.adjustedHours.straightTime }).format("*h:mm [h]")}</TableData>
                                <TableData>{moment.duration({ hours: timeEntry.adjustedHours.overtime }).format("*h:mm [h]")}</TableData>
                                <TableData>{formatCurrency(jobCost)}</TableData>
                            </TableRow>
                        )
                    })}
                </tbody>

                <Footer>
                    <TableRow>
                        <TableData colSpan={5}>{loading && <FaSpinner className="fa-spin" />}</TableData>
                        <TableData>{formatCurrency(totals?.rate)}</TableData>
                        <TableData>{moment.duration({ hours: totals?.hours }).format("*h:mm [h]")}</TableData>
                        <TableData>{moment.duration({ hours: totals?.straightTime }).format("*h:mm [h]")}</TableData>
                        <TableData>{moment.duration({ hours: totals?.overtime }).format("*h:mm [h]")}</TableData>
                        <TableData>{formatCurrency(totals?.jobCost)}</TableData>
                    </TableRow>
                </Footer>
            </Table>
            {showEdit && <ModalEditTimesheet onClose={() => setShowEdit(false)} timesheet={timesheet} />}
            {
                showConfirm && <ModalConfirm
                    onConfirm={onDeleteTimesheet}
                    onClose={() => setShowConfirm(false)}
                    description="Are you sure you want to delete this timesheet?  All associated time entries will also be deleted."
                />
            }
        </Modal>
    );
};

export default ModalTimesheet;
